import { isEqual } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

import { assignInitialSelectedProducts } from './utils';
import { getIdFromGid } from '../../subscriptions/utils';
import { getGidFromIdForResource } from '../../../utils/gid-utils';

export const useBundleSectionsSelection = ({
  bundleStep,
  addProduct,
  removeProduct,
  setSelectedProducts,
  sections = [],
  productsInSections,
  initialSelectedProducts = {},
}: {
  bundleStep: number;
  addProduct: (product: GlobalVariant | GlobalProduct) => void;
  removeProduct: (product: GlobalVariant | GlobalProduct) => void;
  productsInSections?: BabSection[];
  setSelectedProducts: (newSelectedProducts: Record<string, number>) => void;
  sections?: {
    Id: string;
    Products: IndividualProductData[];
  }[];
  initialSelectedProducts?: Record<string, number>;
}) => {
  const [selectedProductsBySection, setSelectedProductsBySection] = useState(
    () =>
      assignInitialSelectedProducts(
        initialSelectedProducts,
        productsInSections,
      ),
  );

  useEffect(() => {
    const newSelectedProductsBySection = assignInitialSelectedProducts(
      initialSelectedProducts,
      productsInSections,
    );

    if (!isEqual(newSelectedProductsBySection, selectedProductsBySection)) {
      setSelectedProductsBySection(() => newSelectedProductsBySection);
    }
  }, [initialSelectedProducts, productsInSections]);

  const addProductToSection = useCallback(
    (sectionId, product) => {
      addProduct(product);

      setSelectedProductsBySection({
        ...selectedProductsBySection,
        [sectionId]: {
          ...(selectedProductsBySection[sectionId] || {}),
          [product.id]:
            (selectedProductsBySection[sectionId]?.[product.id] || 0) +
            (bundleStep || 1),
        },
      });
    },
    [addProduct, bundleStep, selectedProductsBySection],
  );

  const removeProductFromSection = useCallback(
    (sectionId, product) => {
      removeProduct(product);

      setSelectedProductsBySection({
        ...selectedProductsBySection,
        [sectionId]: {
          ...(selectedProductsBySection[sectionId] || {}),
          [product.id]:
            selectedProductsBySection[sectionId]?.[product.id] > 0
              ? (selectedProductsBySection[sectionId]?.[product.id] || 0) - 1
              : 0,
        },
      });
    },
    [removeProduct, selectedProductsBySection],
  );

  const handleSetSelectedProductsBySection = useCallback(
    (sectionId, newSelectedProducts: Record<string, number>) => {
      setSelectedProducts(newSelectedProducts);
      const isCollections = productsInSections?.some(
        (section) => section?.collections?.length > 0,
      );
      const products = isCollections
        ? productsInSections
            ?.find((section) => section?.id === sectionId)
            ?.productsInCollections?.flat()
            .map(({ id: productId, productData }) => {
              return {
                Id: productId?.toString(),
                Handle: productData?.handle,
                Name: productData?.title,
                Variants: productData?.variants?.map(
                  ({ id: VariantId, title }) => {
                    return {
                      VariantId: getGidFromIdForResource(
                        VariantId,
                        'ProductVariant',
                      ),
                      VariantName: title,
                    };
                  },
                ),
              };
            })
        : sections?.find((section) => section?.Id === sectionId)?.Products ||
          [];

      setSelectedProductsBySection({
        ...selectedProductsBySection,
        [sectionId]: addProductsFromTheSection(newSelectedProducts, products),
      });
    },
    [
      setSelectedProducts,
      productsInSections,
      sections,
      selectedProductsBySection,
    ],
  );

  return {
    addProductToSection,
    removeProductFromSection,
    handleSetSelectedProductsBySection,
    selectedProductsBySection,
  };
};

const addProductsFromTheSection = (
  newSelectedProducts: Record<string, number> = {},
  products: IndividualProductData[],
): Record<string, number> => {
  const newProducts = {};
  const productsIds = products.flatMap(({ Variants }) =>
    Variants.map(({ VariantId }) => getIdFromGid(VariantId)),
  );

  Object.keys(newSelectedProducts).forEach((productId) => {
    if (productsIds.includes(productId)) {
      newProducts[productId] = newSelectedProducts[productId];
    }
  });

  return newProducts;
};
